import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiClient } from "@/lib/api";

const ALLOWED_QUOTE_ASSETS = new Set(["USDT", "USDC"]);

const MODE_OPTIONS = [
  { value: "all_market_symbols", label: "Tüm market sembolleri" },
  { value: "top_volume", label: "Hacme göre üst semboller" },
  { value: "manual_selection", label: "Manual seçim" },
];

const SOURCE_OPTIONS = [
  { value: "crypto", label: "Kripto" },
  { value: "stock", label: "Senet" },
];

const normalizeModeValue = (mode) => {
  const raw = String(mode || "all_market_symbols").toLowerCase();
  if (raw === "all_exchange") {
    return "all_market_symbols";
  }
  if (raw === "top_active_50" || raw === "top_active_100") {
    return "top_volume";
  }
  if (raw === "custom_list" || raw === "bot_scope") {
    return "manual_selection";
  }
  if (["all_market_symbols", "top_volume", "manual_selection"].includes(raw)) {
    return raw;
  }
  return "all_market_symbols";
};

const normalizeSymbols = (symbols) => {
  if (!Array.isArray(symbols)) {
    return [];
  }
  const normalized = symbols.map((item) => String(item || "").trim().toUpperCase()).filter(Boolean);
  return Array.from(new Set(normalized));
};

const areSameSymbolSet = (left, right) => {
  const leftNormalized = normalizeSymbols(left).sort();
  const rightNormalized = normalizeSymbols(right).sort();
  if (leftNormalized.length !== rightNormalized.length) {
    return false;
  }
  return leftNormalized.every((value, index) => value === rightNormalized[index]);
};

const detectQuoteAsset = (symbol) => {
  const normalized = String(symbol || "").trim().toUpperCase();
  if (normalized.endsWith("USDT")) return "USDT";
  if (normalized.endsWith("USDC")) return "USDC";
  return "UNKNOWN";
};

const liquidityBandFromVolume = (volume) => {
  const numeric = Number(volume || 0);
  if (numeric >= 50_000_000) return "high";
  if (numeric >= 10_000_000) return "medium";
  return "low";
};

const riskBandFromVolume = (volume) => {
  const liquidityBand = liquidityBandFromVolume(volume);
  if (liquidityBand === "high") return "low";
  if (liquidityBand === "medium") return "medium";
  return "high";
};

export const SymbolSelectorPanel = ({
  testIdPrefix,
  exchange = "binance",
  marketType = "",
  quoteAssetFilter = "ALL",
  multi = true,
  selectedSymbols,
  onSelectedSymbolsChange,
  source,
  onSourceChange,
  mode,
  onModeChange,
  onMarketTypeChange,
  onWatchlistApplied,
  selectionSummary,
  onPersistSelection,
  onApplyScope,
  onChangeList,
  onLoadSavedList,
}) => {
  const [search, setSearch] = useState("");
  const [symbols, setSymbols] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadWarning, setLoadWarning] = useState("");
  const [universeMeta, setUniverseMeta] = useState({
    status: "ok",
    source: "live",
    is_partial: false,
    generated_at: null,
  });
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlistId, setSelectedWatchlistId] = useState("");
  const [watchlistName, setWatchlistName] = useState("");
  const [isDeletingWatchlist, setIsDeletingWatchlist] = useState(false);
  const [isPersistingScope, setIsPersistingScope] = useState(false);
  const [providerConfig, setProviderConfig] = useState(null);
  const [liquidityBandFilter, setLiquidityBandFilter] = useState("all");
  const [riskBandFilter, setRiskBandFilter] = useState("all");
  const [exchangeFilter, setExchangeFilter] = useState("all");
  const [savedSelection, setSavedSelection] = useState(() => normalizeSymbols(selectedSymbols));
  const [draftSelection, setDraftSelection] = useState(() => normalizeSymbols(selectedSymbols));
  const loadAbortRef = useRef(null);
  const lastContextKeyRef = useRef("");
  const lastWarningHashRef = useRef("");

  const incomingSelectedSymbols = useMemo(() => normalizeSymbols(selectedSymbols), [selectedSymbols]);
  const normalizedMode = useMemo(() => normalizeModeValue(mode), [mode]);
  const normalizedMarketType = String(marketType || "").trim().toLowerCase();
  const hasMarketScope = normalizedMarketType === "spot" || normalizedMarketType === "futures" || normalizedMarketType === "both";
  const universeMarketType = normalizedMarketType === "both" ? "spot" : normalizedMarketType;
  const safeRows = useMemo(() => (Array.isArray(symbols) ? symbols : (Array.isArray(rows) ? rows : [])), [symbols, rows]);
  const safeWatchlists = useMemo(() => (Array.isArray(watchlists) ? watchlists : []), [watchlists]);
  const safeSourceOptions = useMemo(() => (Array.isArray(SOURCE_OPTIONS) ? SOURCE_OPTIONS : []), []);
  const safeModeOptions = useMemo(() => (Array.isArray(MODE_OPTIONS) ? MODE_OPTIONS : []), []);

  const emitSelectionChange = useCallback((next) => {
    if (typeof onSelectedSymbolsChange === "function") {
      onSelectedSymbolsChange(Array.isArray(next) ? next : []);
    }
  }, [onSelectedSymbolsChange]);

  const normalizedRows = useMemo(
    () => safeRows.slice(0, 300).map((row) => {
      const symbol = String(row?.symbol || "").trim().toUpperCase();
      const quoteAsset = String(row?.quote_asset || detectQuoteAsset(symbol)).trim().toUpperCase() || "UNKNOWN";
      const unsupported = String(source || "crypto").toLowerCase() !== "crypto" || !ALLOWED_QUOTE_ASSETS.has(quoteAsset);
      return {
        ...row,
        symbol,
        quote_asset: quoteAsset,
        unsupported,
      };
    }).filter((row) => Boolean(row.symbol)),
    [safeRows, source],
  );

  const depthFilteredRows = useMemo(() => {
    return normalizedRows.filter((row) => {
      const liquidityBand = liquidityBandFromVolume(row.volume_24h);
      const riskBand = riskBandFromVolume(row.volume_24h);
      const rowExchange = String(row.exchange || "").toLowerCase();

      if (exchangeFilter !== "all" && rowExchange !== exchangeFilter) return false;
      if (liquidityBandFilter !== "all" && liquidityBand !== liquidityBandFilter) return false;
      if (riskBandFilter !== "all" && riskBand !== riskBandFilter) return false;
      return true;
    });
  }, [normalizedRows, exchangeFilter, liquidityBandFilter, riskBandFilter]);

  const exchangeOptions = useMemo(() => {
    const values = Array.from(new Set(normalizedRows.map((row) => String(row.exchange || "").toLowerCase()).filter(Boolean)));
    return ["all", ...values];
  }, [normalizedRows]);

  const normalizedSearch = String(search || "").trim().toUpperCase();

  const isDraftDirty = useMemo(
    () => !areSameSymbolSet(draftSelection, savedSelection),
    [draftSelection, savedSelection],
  );

  useEffect(() => {
    const contextKey = `${String(source || "crypto").toLowerCase()}|${normalizedMarketType}`;
    const contextChanged = contextKey !== lastContextKeyRef.current;
    if (contextChanged) {
      lastContextKeyRef.current = contextKey;
      setSavedSelection(incomingSelectedSymbols);
      setDraftSelection(incomingSelectedSymbols);
      return;
    }
    setSavedSelection(incomingSelectedSymbols);
    setDraftSelection((prev) => (isDraftDirty ? prev : incomingSelectedSymbols));
  }, [incomingSelectedSymbols, isDraftDirty, normalizedMarketType, source]);

  const loadProviderConfig = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/symbol-selector/provider-config");
      setProviderConfig(data || null);
    } catch {
      setProviderConfig(null);
    }
  }, []);

  const loadWatchlists = useCallback(async () => {
    if (!hasMarketScope) {
      setWatchlists([]);
      return;
    }
    try {
      const { data } = await apiClient.get("/symbol-selector/watchlists", { params: { source, market_type: universeMarketType } });
      const next = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
      setWatchlists(next);
    } catch {
      setWatchlists([]);
    }
  }, [hasMarketScope, source, universeMarketType]);

  const loadUniverse = useCallback(async ({ forceMode } = {}) => {
    if (!hasMarketScope) {
      setRows([]);
      setSymbols([]);
      setLoadWarning("Önce market seçin (Spot/Futures).");
      return;
    }
    setIsLoading(true);
    try {
      if (loadAbortRef.current) {
        loadAbortRef.current.abort();
      }
      loadAbortRef.current = new AbortController();
      const activeMode = normalizeModeValue(forceMode || "manual_selection");
      const { data } = await apiClient.get("/symbol-selector/universe", {
        params: {
          source,
          exchange,
          market_type: universeMarketType,
          mode: activeMode,
          quote_asset_filter: quoteAssetFilter,
        },
        signal: loadAbortRef.current.signal,
      });
      const universeRows = Array.isArray(data?.rows) ? data.rows : [];
      setRows(universeRows);
      setSymbols(universeRows);
      const statusValue = String(data?.status || "ok").trim().toLowerCase() || "ok";
      const sourceValue = String(data?.source || "live").trim().toLowerCase() || "live";
      const isPartial = Boolean(data?.is_partial);
      const generatedAt = String(data?.generated_at || "").trim() || null;
      const warnings = Array.isArray(data?.warnings) ? data.warnings.map((item) => String(item || "").trim()).filter(Boolean) : [];

      setUniverseMeta({
        status: statusValue,
        source: sourceValue,
        is_partial: isPartial,
        generated_at: generatedAt,
      });

      if (statusValue !== "ok" || isPartial || warnings.length > 0) {
        let warningText = "Liste şu an tam güncellenemedi.";
        if (sourceValue === "cache") {
          warningText = "Liste kısmi olabilir. Son başarılı veri gösteriliyor.";
        } else if (sourceValue === "empty_fallback") {
          warningText = "Liste şu an tam güncellenemedi. Geçici boş fallback gösteriliyor.";
        } else if (statusValue === "partial") {
          warningText = "Liste kısmi olabilir.";
        }
        setLoadWarning(warningText);

        const warningHash = `${statusValue}|${sourceValue}|${warnings.join(",")}`;
        if (warningHash !== lastWarningHashRef.current) {
          toast.warning(warningText);
          lastWarningHashRef.current = warningHash;
        }
      } else {
        setLoadWarning("");
        lastWarningHashRef.current = "";
      }

      if (Array.isArray(data?.warnings) && data.warnings.includes("alpha_vantage_key_missing") && source === "stock") {
        toast.warning("Senet listesi için Alpha Vantage API key gerekli (Admin > Market Universe). ");
      }

    } catch (error) {
      if (error?.code === "ERR_CANCELED" || String(error?.message || "").toLowerCase().includes("canceled")) {
        return;
      }
      const warningText = error?.response?.data?.detail || "Sembol evreni yüklenemedi. Son başarılı liste korunuyor.";
      setLoadWarning(String(warningText));
      setUniverseMeta((prev) => ({
        ...prev,
        status: "error",
        source: "empty_fallback",
        is_partial: true,
        generated_at: prev?.generated_at || null,
      }));
      toast.warning(warningText);
    } finally {
      setIsLoading(false);
    }
  }, [
    exchange,
    hasMarketScope,
    universeMarketType,
    quoteAssetFilter,
    source,
  ]);

  useEffect(() => {
    loadProviderConfig();
    loadWatchlists();
  }, [loadProviderConfig, loadWatchlists]);

  useEffect(() => {
    loadUniverse();
  }, [loadUniverse, normalizedMarketType, source]);

  const toggleSymbol = (row) => {
    const symbol = String(row?.symbol || "").trim().toUpperCase();
    if (!symbol || row?.unsupported) {
      return;
    }
    const nextSet = new Set(draftSelection);
    if (nextSet.has(symbol)) {
      nextSet.delete(symbol);
    } else if (!multi) {
      nextSet.clear();
      nextSet.add(symbol);
    } else {
      nextSet.add(symbol);
    }
    const next = Array.from(nextSet);
    setDraftSelection(next);
  };

  const displayRows = useMemo(
    () => depthFilteredRows
      .filter((row) => !row.unsupported)
      .filter((row) => (normalizedSearch ? String(row.symbol || "").includes(normalizedSearch) : true)),
    [depthFilteredRows, normalizedSearch],
  );

  const visibleSymbols = useMemo(
    () => displayRows.map((row) => row.symbol),
    [displayRows],
  );

  const allVisibleSelected = useMemo(
    () => visibleSymbols.length > 0 && visibleSymbols.every((symbol) => draftSelection.includes(symbol)),
    [visibleSymbols, draftSelection],
  );

  const selectAllVisible = () => {
    if (!multi) {
      setDraftSelection(visibleSymbols.slice(0, 1));
      return;
    }
    const merged = Array.from(new Set([...(draftSelection || []), ...visibleSymbols]));
    setDraftSelection(merged);
  };

  const clearAllSelected = () => {
    setDraftSelection([]);
  };

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      const next = draftSelection.filter((symbol) => !visibleSymbols.includes(symbol));
      setDraftSelection(next);
      return;
    }
    selectAllVisible();
  };

  const saveDraftSelection = async () => {
    if (!hasMarketScope) {
      toast.error("Önce market seçin (Spot/Futures)");
      return;
    }
    const normalized = normalizeSymbols(draftSelection);
    const target = multi ? normalized : normalized.slice(0, 1);
    const selectedWatchlist = safeWatchlists.find((item) => item.id === selectedWatchlistId);
    const modeToken = normalizeModeValue(normalizedMode);
    const resolvedListName = selectedWatchlist?.name
      || selectionSummary?.selected_list_name
      || (modeToken === "manual_selection" ? "manual-selection" : modeToken === "top_volume" ? "top-volume" : "all-market");

    if (typeof onPersistSelection === "function") {
      setIsPersistingScope(true);
      try {
        const persistResult = await onPersistSelection({
          symbol_source: String(source || "crypto").toLowerCase(),
          symbol_selection_mode: modeToken,
          selected_symbols: target,
          list_name: String(resolvedListName || "").trim() || null,
          market_type: normalizedMarketType,
        });
        if (persistResult === false) {
          return;
        }
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const message = typeof detail === "string" ? detail : "Scope backend'e kaydedilemedi";
        toast.error(message);
        return;
      } finally {
        setIsPersistingScope(false);
      }
    }

    setDraftSelection(target);
    setSavedSelection(target);
    emitSelectionChange(target);
    if (typeof onApplyScope === "function") {
      onApplyScope(target);
    }
  };

  const rollbackDraftSelection = () => {
    setDraftSelection(savedSelection);
    toast.info("Taslak seçim geri alındı");
  };

  const saveWatchlist = async () => {
    const trimmed = watchlistName.trim();
    if (!trimmed) {
      toast.error("Watchlist adı zorunlu");
      return;
    }
    if (draftSelection.length === 0) {
      toast.error("Watchlist için en az bir sembol seçin");
      return;
    }
    try {
      await apiClient.post("/symbol-selector/watchlists", {
        name: trimmed,
        source,
        exchange,
        market_type: universeMarketType,
        symbols: draftSelection,
      });
      setWatchlistName("");
      toast.success("Watchlist kaydedildi");
      await loadWatchlists();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Watchlist kaydedilemedi");
    }
  };

  const applyWatchlist = () => {
    const selected = safeWatchlists.find((item) => item.id === selectedWatchlistId);
    if (!selected) {
      return;
    }
    const symbols = normalizeSymbols(selected.symbols || []).filter(
      (symbol) => symbol.endsWith("USDT") || symbol.endsWith("USDC"),
    );
    if (symbols.length === 0) {
      toast.error("Seçili listede geçerli USDT/USDC market bulunamadı");
      return;
    }
    setDraftSelection(multi ? symbols : symbols.slice(0, 1));
    if (typeof onWatchlistApplied === "function") {
      onWatchlistApplied(symbols);
    }
    toast.success(`${selected.name} taslağa uygulandı. Kaydet ile onaylayın.`);
  };

  const deleteWatchlist = async () => {
    if (!selectedWatchlistId) {
      return;
    }

    const selected = safeWatchlists.find((item) => item.id === selectedWatchlistId);
    const name = selected?.name || "watchlist";
    const approved = typeof window === "undefined" ? true : window.confirm(`${name} listesini silmek istiyor musunuz?`);
    if (!approved) {
      return;
    }

    setIsDeletingWatchlist(true);
    try {
      await apiClient.delete(`/symbol-selector/watchlists/${selectedWatchlistId}`);
      toast.success("Watchlist silindi");
      setSelectedWatchlistId("");
      await loadWatchlists();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Watchlist silinemedi");
    } finally {
      setIsDeletingWatchlist(false);
    }
  };

  return (
    <div className="space-y-3 rounded border border-slate-700 bg-slate-950 p-3" data-testid={`${testIdPrefix}-panel`}>
      <div className="grid gap-2 md:grid-cols-5" data-testid={`${testIdPrefix}-controls-grid`}>
        <label className="space-y-1" data-testid={`${testIdPrefix}-market-field`}>
          <span className="text-xs text-slate-400">Market Scope</span>
          <select
            value={normalizedMarketType}
            onChange={(event) => (typeof onMarketTypeChange === "function" ? onMarketTypeChange(event.target.value) : null)}
            className="h-9 w-full rounded border border-slate-700 bg-black px-2 text-sm"
            data-testid={`${testIdPrefix}-market-select`}
          >
            <option value="" data-testid={`${testIdPrefix}-market-option-empty`}>seçiniz</option>
            <option value="spot" data-testid={`${testIdPrefix}-market-option-spot`}>Spot</option>
            <option value="futures" data-testid={`${testIdPrefix}-market-option-futures`}>Futures</option>
            <option value="both" data-testid={`${testIdPrefix}-market-option-both`}>Both</option>
          </select>
        </label>

        <label className="space-y-1" data-testid={`${testIdPrefix}-source-field`}>
          <span className="text-xs text-slate-400">Kaynak</span>
          <select value={source} onChange={(event) => (typeof onSourceChange === "function" ? onSourceChange(event.target.value) : null)} className="h-9 w-full rounded border border-slate-700 bg-black px-2 text-sm" data-testid={`${testIdPrefix}-source-select`}>
            {safeSourceOptions.map((item) => (
              <option key={item.value} value={item.value} data-testid={`${testIdPrefix}-source-option-${item.value}`}>{item.label}</option>
            ))}
          </select>
        </label>

        <label className="space-y-1" data-testid={`${testIdPrefix}-mode-field`}>
          <span className="text-xs text-slate-400">Seçim Modu</span>
          <select value={normalizedMode} onChange={(event) => (typeof onModeChange === "function" ? onModeChange(event.target.value) : null)} className="h-9 w-full rounded border border-slate-700 bg-black px-2 text-sm" data-testid={`${testIdPrefix}-mode-select`}>
            {safeModeOptions.map((item) => (
              <option key={item.value} value={item.value} data-testid={`${testIdPrefix}-mode-option-${item.value}`}>{item.label}</option>
            ))}
          </select>
        </label>

        <label className="space-y-1" data-testid={`${testIdPrefix}-search-field`}>
          <span className="text-xs text-slate-400">Sembol Ara</span>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value.toUpperCase())}
            placeholder="ETHUSDT / SOLUSDC"
            data-testid={`${testIdPrefix}-search-input`}
          />
        </label>

        <div className="flex items-end gap-2" data-testid={`${testIdPrefix}-actions-row`}>
          <Button onClick={() => loadUniverse({ forceMode: normalizedMode })} disabled={isLoading || !hasMarketScope} data-testid={`${testIdPrefix}-refresh-button`}>
            {isLoading ? "Yükleniyor..." : "Listele"}
          </Button>
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-3" data-testid={`${testIdPrefix}-depth-filters-grid`}>
        <label className="space-y-1" data-testid={`${testIdPrefix}-depth-liquidity-field`}>
          <span className="text-xs text-slate-400">Liquidity Band</span>
          <select
            value={liquidityBandFilter}
            onChange={(event) => setLiquidityBandFilter(event.target.value)}
            className="h-9 w-full rounded border border-slate-700 bg-black px-2 text-sm"
            data-testid={`${testIdPrefix}-depth-liquidity-select`}
          >
            <option value="all">all</option>
            <option value="high">high</option>
            <option value="medium">medium</option>
            <option value="low">low</option>
          </select>
        </label>

        <label className="space-y-1" data-testid={`${testIdPrefix}-depth-risk-field`}>
          <span className="text-xs text-slate-400">Risk Band</span>
          <select
            value={riskBandFilter}
            onChange={(event) => setRiskBandFilter(event.target.value)}
            className="h-9 w-full rounded border border-slate-700 bg-black px-2 text-sm"
            data-testid={`${testIdPrefix}-depth-risk-select`}
          >
            <option value="all">all</option>
            <option value="high">high</option>
            <option value="medium">medium</option>
            <option value="low">low</option>
          </select>
        </label>

        <label className="space-y-1" data-testid={`${testIdPrefix}-depth-exchange-field`}>
          <span className="text-xs text-slate-400">Exchange</span>
          <select
            value={exchangeFilter}
            onChange={(event) => setExchangeFilter(event.target.value)}
            className="h-9 w-full rounded border border-slate-700 bg-black px-2 text-sm"
            data-testid={`${testIdPrefix}-depth-exchange-select`}
          >
            {exchangeOptions.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid gap-2 md:grid-cols-3" data-testid={`${testIdPrefix}-watchlist-grid`}>
        <label className="space-y-1" data-testid={`${testIdPrefix}-watchlist-select-field`}>
          <span className="text-xs text-slate-400">Kayıtlı Liste</span>
          <select value={selectedWatchlistId} onChange={(event) => setSelectedWatchlistId(event.target.value)} className="h-9 w-full rounded border border-slate-700 bg-black px-2 text-sm" data-testid={`${testIdPrefix}-watchlist-select`}>
            <option value="" data-testid={`${testIdPrefix}-watchlist-option-empty`}>seçiniz</option>
            {safeWatchlists.map((item) => (
              <option key={item.id} value={item.id} data-testid={`${testIdPrefix}-watchlist-option-${item.id}`}>{item.name}</option>
            ))}
          </select>
        </label>
        <div className="flex items-end gap-2" data-testid={`${testIdPrefix}-watchlist-apply-wrapper`}>
          <Button variant="outline" onClick={applyWatchlist} disabled={!selectedWatchlistId} data-testid={`${testIdPrefix}-watchlist-apply-button`}>Listeyi Uygula</Button>
          <Button
            variant="outline"
            onClick={deleteWatchlist}
            disabled={!selectedWatchlistId || isDeletingWatchlist}
            data-testid={`${testIdPrefix}-watchlist-delete-button`}
          >
            {isDeletingWatchlist ? "Siliniyor..." : "Sil"}
          </Button>
        </div>
        <label className="space-y-1" data-testid={`${testIdPrefix}-watchlist-name-field`}>
          <span className="text-xs text-slate-400">Yeni Liste Adı</span>
          <div className="flex gap-2" data-testid={`${testIdPrefix}-watchlist-save-row`}>
            <Input value={watchlistName} onChange={(event) => setWatchlistName(event.target.value)} placeholder="örn: swing-izleme" data-testid={`${testIdPrefix}-watchlist-name-input`} />
            <Button variant="outline" onClick={saveWatchlist} data-testid={`${testIdPrefix}-watchlist-save-button`}>Kaydet</Button>
          </div>
        </label>
      </div>

      {source === "stock" && !providerConfig?.has_alpha_vantage_key && (
        <p className="text-xs text-amber-300" data-testid={`${testIdPrefix}-stock-key-warning`}>
          Senet evreni için Alpha Vantage API key gerekli. Admin &gt; Market Universe ekranından ekleyebilirsiniz.
        </p>
      )}

      {loadWarning && (
        <p className="text-xs text-amber-300" data-testid={`${testIdPrefix}-load-warning`}>
          {loadWarning}
        </p>
      )}

      <p className="text-[11px] text-slate-400" data-testid={`${testIdPrefix}-universe-meta`}>
        status: {String(universeMeta.status || "ok")} · source: {String(universeMeta.source || "live")} · partial: {String(Boolean(universeMeta.is_partial))} · generated_at: {universeMeta.generated_at || "-"}
      </p>

      <div className="rounded border border-slate-800 bg-slate-900/70 p-3 text-xs text-slate-200" data-testid={`${testIdPrefix}-active-selection-summary`}>
        <p className="font-semibold text-cyan-200" data-testid={`${testIdPrefix}-active-selection-summary-title`}>Active Selection Summary</p>
        <div className="mt-2 grid gap-1 sm:grid-cols-2" data-testid={`${testIdPrefix}-active-selection-summary-grid`}>
          <p data-testid={`${testIdPrefix}-active-selection-count`}>Selected count: <strong>{draftSelection.length}</strong></p>
          <p data-testid={`${testIdPrefix}-active-selection-list-name`}>List name: <strong>{selectionSummary?.selected_list_name || "manual"}</strong></p>
          <p data-testid={`${testIdPrefix}-active-selection-source-mode`}>Source mode: <strong>{source || "crypto"}</strong></p>
          <p data-testid={`${testIdPrefix}-active-selection-updated-at`}>Last updated: <strong>{selectionSummary?.last_updated || universeMeta.generated_at || "-"}</strong></p>
          <p className="sm:col-span-2" data-testid={`${testIdPrefix}-active-selection-preview`}>
            örnek 3 sembol: <strong>{(draftSelection.slice(0, 3).join(", ") || "-")}</strong>
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2" data-testid={`${testIdPrefix}-bulk-actions-row`}>
        <p className="text-xs text-slate-400" data-testid={`${testIdPrefix}-selected-count`}>Draft/Saved: {draftSelection.length}/{savedSelection.length}</p>
        <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-bulk-actions-buttons`}>
          <Button type="button" variant="outline" onClick={selectAllVisible} data-testid={`${testIdPrefix}-select-all-button`}>
            Select All
          </Button>
          <Button type="button" variant="outline" onClick={clearAllSelected} data-testid={`${testIdPrefix}-clear-all-button`}>
            Clear All
          </Button>
          <Button type="button" variant="outline" onClick={rollbackDraftSelection} disabled={!isDraftDirty} data-testid={`${testIdPrefix}-rollback-button`}>
            Geri Al
          </Button>
          <Button type="button" onClick={saveDraftSelection} disabled={!isDraftDirty || isPersistingScope} data-testid={`${testIdPrefix}-save-button`}>
            {isPersistingScope ? "Kaydediliyor..." : "Scope Uygula"}
          </Button>
          <Button type="button" variant="outline" onClick={onChangeList} data-testid={`${testIdPrefix}-change-list-button`}>Listeyi Değiştir</Button>
          <Button type="button" variant="outline" onClick={() => { if (typeof onLoadSavedList === "function") onLoadSavedList(); applyWatchlist(); }} data-testid={`${testIdPrefix}-load-saved-list-button`}>
            Kaydedilmiş Listeyi Yükle
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-800 bg-slate-900/40 px-3 py-2" data-testid={`${testIdPrefix}-table-search-row`}>
        <div className="flex flex-1 items-center gap-2 min-w-[220px]">
          <span className="text-xs uppercase tracking-wide text-slate-400 shrink-0">Tabloda Ara</span>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value.toUpperCase())}
            placeholder="ETHUSDT, BTC, SOL..."
            className="h-8 max-w-xs"
            data-testid={`${testIdPrefix}-table-search-input`}
          />
          {search && (
            <Button
              type="button"
              variant="outline"
              className="h-8 px-2 text-xs"
              onClick={() => setSearch("")}
              data-testid={`${testIdPrefix}-table-search-clear`}
            >
              Temizle
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-400" data-testid={`${testIdPrefix}-table-search-result-count`}>
          {Array.isArray(displayRows) ? displayRows.length : 0} / {Array.isArray(depthFilteredRows) ? depthFilteredRows.length : 0} sembol gösteriliyor
        </p>
      </div>

      <div className="max-h-52 overflow-auto rounded border border-slate-800" data-testid={`${testIdPrefix}-rows-wrapper`}>
        <table className="min-w-full text-xs" data-testid={`${testIdPrefix}-rows-table`}>
          <thead className="sticky top-0 bg-slate-900" data-testid={`${testIdPrefix}-rows-head`}>
            <tr>
              <th className="px-2 py-1 text-left" data-testid={`${testIdPrefix}-header-select-all`}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  data-testid={`${testIdPrefix}-header-select-all-checkbox`}
                />
              </th>
              <th className="px-2 py-1 text-left">Symbol</th>
              <th className="px-2 py-1 text-left" data-testid={`${testIdPrefix}-header-quote-asset`}>Quote Asset</th>
              <th className="px-2 py-1 text-left">Exchange</th>
              <th className="px-2 py-1 text-left">Vol 24h</th>
              <th className="px-2 py-1 text-left" data-testid={`${testIdPrefix}-header-policy`}>Strategy Policy</th>
            </tr>
          </thead>
          <tbody data-testid={`${testIdPrefix}-rows-body`}>
            {isLoading && (
              <tr data-testid={`${testIdPrefix}-rows-loading`}>
                <td colSpan={6} className="px-2 py-3 text-center text-slate-400" data-testid={`${testIdPrefix}-rows-loading-text`}>
                  Yükleniyor...
                </td>
              </tr>
            )}
            {(Array.isArray(displayRows) ? displayRows : []).map((row, index) => {
              const checked = draftSelection.includes(row.symbol);
              return (
                <tr
                  key={`${row.symbol}-${index}`}
                  className={`border-t border-slate-800 ${row.unsupported ? "cursor-not-allowed opacity-50" : ""}`}
                  data-testid={`${testIdPrefix}-row-${index}`}
                >
                  <td className="px-2 py-1" data-testid={`${testIdPrefix}-row-check-${index}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={row.unsupported}
                      onChange={() => toggleSymbol(row)}
                      data-testid={`${testIdPrefix}-row-checkbox-${index}`}
                    />
                  </td>
                  <td className="px-2 py-1" data-testid={`${testIdPrefix}-row-symbol-${index}`}>{row.symbol}</td>
                  <td className="px-2 py-1" data-testid={`${testIdPrefix}-row-quote-asset-${index}`}>{row.quote_asset}</td>
                  <td className="px-2 py-1" data-testid={`${testIdPrefix}-row-exchange-${index}`}>{row.exchange}</td>
                  <td className="px-2 py-1" data-testid={`${testIdPrefix}-row-volume-${index}`}>{row.volume_24h ?? "-"}</td>
                  <td className="px-2 py-1" data-testid={`${testIdPrefix}-row-policy-${index}`}>
                    {row.unsupported ? "UNSUPPORTED_PAIR" : "SUPPORTED"}
                  </td>
                </tr>
              );
            })}
            {!isLoading && (!Array.isArray(displayRows) || displayRows.length === 0) && (
              <tr data-testid={`${testIdPrefix}-rows-empty`}>
                <td colSpan={6} className="px-2 py-3 text-center text-slate-500" data-testid={`${testIdPrefix}-rows-empty-text`}>
                  Gösterilecek sembol yok.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
